import { ButtonDestructive, ButtonNavigationBack } from "@nova/component-btn"
import { stagedFiles, useProgressForSingleUploadFlightId } from "@nova/slice-multifile-upload"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import {
    cancelUpload,
    deselectIconImage,
    leaveUploadPageForIcon,
    selectIconImage,
    selectImageAndUploadIdsForIcon,
    selectIsIconImageSelected,
    selectIsUploadMarkedAsCancelled,
    selectUploadById,
    setPreviewIndex,
} from "plugins/Toolbox/Icons/IconSlice"
import { FC, useCallback, useMemo } from "react"
import { useLazyLoadedImage } from "@nova/toolkit-pure-hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { getIconImageS3Key } from "panels/content/side-panel/upload-flow/constants"
import { selectActivatedFireId } from "features/map/control/selectors"
import Image from "next/image"
import { ImageProcessingState } from "@nova/constants"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { UploadProgressIndicator } from "./InspectorPanelIconLayer"
import * as Icons from "@nova/component-icons"
import { selectUploadTimestampForFlightId } from "features/map/layers/selectors"
import { config } from "@nova/configuration/browser"
import { DateFormat, formatBytes, formatLongDate, TimeFormat } from "@nova/format"
import { setActiveSidePanelModal } from "panels/content/side-panel/slice"
import { selectIconById } from "features/entity-caching/icons/selectors"
import { clearSelection } from "panels/content/side-panel/slice.new"
import { TriCheckbox } from "@nova/component-checkbox"
import { selectIconImageByIdAndIconId } from "features/entity-caching/images/icons/selectors"

export const InspectorPanelIconUpload = () => {
    const dispatch = useAppDispatch()
    const { organizationSettingDateFormat } = config.use("organizationSettingDateFormat")
    const { organizationSettingTimeFormat } = config.use("organizationSettingTimeFormat")
    const {
        flightId: iconId,
        selectUploadStateSummary,
        selectUploadIdsForSet,
        selectUploadSize,
    } = useUploadFlowContext()
    const uploadIds = useAppSelector(selectUploadIdsForSet(iconId))
    const uploadStateSummary = useAppSelector(selectUploadStateSummary(iconId))
    const doneUploadingCount = useMemo(
        () => uploadStateSummary.stateCount[ImageProcessingState.UploadDone],
        [uploadStateSummary.stateCount]
    )
    const isUploadMarkedAsCancelled = useAppSelector(selectIsUploadMarkedAsCancelled(iconId))
    const uploadSize = useAppSelector(selectUploadSize(iconId))
    const timestamp = useAppSelector(selectUploadTimestampForFlightId(iconId))
    const formattedUploadDate = useMemo(
        () =>
            timestamp
                ? formatLongDate({
                      value: new Date(timestamp),
                      dateFormat: organizationSettingDateFormat as DateFormat,
                      timeFormat: organizationSettingTimeFormat as TimeFormat,
                  })
                : "N/A",
        [organizationSettingDateFormat, organizationSettingTimeFormat, timestamp]
    )
    return (
        <div className="flex flex-col gap-2 pb-2 shrink min-h-0">
            <div className="px-4">
                <ButtonNavigationBack
                    title="Back"
                    onClick={() => dispatch(leaveUploadPageForIcon({ id: iconId }))}
                />
            </div>
            <div className="px-4">
                <UploadProgressIndicator />
            </div>
            <ScrollableArea shouldPreventScrollbarShifting>
                <div className="px-4">
                    <ImageGrid
                        entityId={iconId}
                        imageIds={uploadIds}
                        RenderImage={RenderStagedIconImage}
                        allowClick
                    />
                </div>
            </ScrollableArea>
            <div className="px-4">
                <ButtonDestructive
                    title={isUploadMarkedAsCancelled ? "Cancelling..." : "Cancel Upload"}
                    className="w-full"
                    onClick={() => cancelUpload(iconId)}
                    LeftIcon={Icons.CloseCircle}
                    disabled={doneUploadingCount === uploadIds.length || isUploadMarkedAsCancelled}
                />
            </div>
            <div className="px-4 flex flex-col gap-2">
                <div className="w-full h-px bg-gray-700" />
                <div className="w-full flex justify-between">
                    <small className="text-gray-100">Files:</small>
                    <small className="text-gray-300 mono">{uploadIds.length}</small>
                </div>
                <div className="w-full flex justify-between">
                    <small className="text-gray-100">Upload Size:</small>
                    <small className="text-gray-300 mono">{formatBytes(uploadSize)}</small>
                </div>
                <div className="w-full flex justify-between">
                    <small className="text-gray-100">Date Created:</small>
                    <small className="text-gray-300 mono">{formattedUploadDate}</small>
                </div>
            </div>
        </div>
    )
}

export function ImageGrid({
    entityId,
    imageIds,
    RenderImage,
    allowClick = false,
    allowSelect = false,
}: {
    entityId: string
    imageIds: string[]
    RenderImage: FC<{ entityId: string; imageId: string; allowClick: boolean }>
    allowClick?: boolean
    allowSelect?: boolean
}) {
    const rows = useMemo(
        () =>
            imageIds.reduce(
                (acc, curImageId) => {
                    const curRow = acc.at(-1)!
                    if (curRow.length < 3) {
                        curRow.push(curImageId)
                        return [...acc.slice(0, -1), curRow]
                    }
                    return [...acc, [curImageId]]
                },
                [[]] as string[][]
            ),
        [imageIds]
    )
    return (
        <div className="flex flex-col gap-2">
            {rows.map((row, i) => (
                <ImageRow
                    key={i}
                    entityId={entityId}
                    imageIds={row}
                    isOnlyRow={rows.length === 1}
                    RenderImage={RenderImage}
                    allowClick={allowClick}
                    allowSelect={allowSelect}
                />
            ))}
        </div>
    )
}

function ImageRow({
    entityId,
    imageIds,
    isOnlyRow,
    RenderImage,
    allowClick,
    allowSelect,
}: {
    entityId: string
    imageIds: string[]
    isOnlyRow: boolean
    RenderImage: FC<{
        entityId: string
        imageId: string
        allowClick: boolean
        allowSelect: boolean
    }>
    allowClick: boolean
    allowSelect: boolean
}) {
    if (!imageIds.length) return null
    return (
        <div
            className="flex w-full justify-between"
            style={{
                height:
                    imageIds.length === 1 && isOnlyRow
                        ? "150px"
                        : imageIds.length <= 2
                          ? "122px"
                          : "80px",
            }}
        >
            {imageIds.map((imageId) => {
                return (
                    <div
                        key={imageId}
                        className={classNames(
                            "h-full overflow-hidden rounded cursor-pointer relative",
                            {
                                "aspect-square": imageIds.length > 1,
                                "w-full": imageIds.length === 1,
                            }
                        )}
                    >
                        <RenderImage
                            entityId={entityId}
                            imageId={imageId}
                            allowClick={allowClick}
                            allowSelect={allowSelect}
                        />
                    </div>
                )
            })}
        </div>
    )
}

export function RenderStagedIconImage({
    entityId,
    imageId,
    includeOverlay = true,
    allowClick = false,
    display = "cover",
}: {
    entityId: string
    imageId: string
    includeOverlay?: boolean
    allowClick?: boolean
    display?: "cover" | "contain"
}) {
    const dispatch = useAppDispatch()
    const upload = useAppSelector(selectUploadById(entityId, imageId))
    const imageAndUploads = useAppSelector(selectImageAndUploadIdsForIcon(entityId))
    const fetchBlob = useCallback(async () => stagedFiles[imageId], [imageId])
    const uploadProgress = useProgressForSingleUploadFlightId(imageId)
    const { blobUrl, isFetching, containerRef, isError } = useLazyLoadedImage({
        type: "IconImage",
        imageKey: imageId,
        fetchBlob,
    })
    const overlayWidth = useMemo(() => {
        if (!upload) return "100%"
        if ("state" in upload) {
            switch (upload.state) {
                case ImageProcessingState.Pending:
                    return "100%"
                case ImageProcessingState.Processing:
                    return "90%"
                case ImageProcessingState.ProcessingDone:
                    return "80%"
                case ImageProcessingState.PendingUpload:
                    return "70%"
                case ImageProcessingState.Uploading:
                    return `${Math.max(0, 50 * (1 - uploadProgress))}%`
                default:
                    return "0%"
            }
        }
    }, [upload, uploadProgress])
    return (
        <div
            ref={containerRef}
            className="relative w-full h-full bg-[#1D1D21]"
            onClick={() => {
                if (!allowClick) return
                dispatch(
                    setPreviewIndex({
                        index: Math.max(
                            0,
                            imageAndUploads.findIndex((image) => image.id === imageId)
                        ),
                    })
                )
                dispatch(
                    setActiveSidePanelModal({
                        id: entityId,
                        layerContentType: "iconimage",
                        type: "layer",
                    })
                )
                dispatch(clearSelection())
            }}
        >
            {includeOverlay && (
                <div
                    className="absolute right-0 top-0 bottom-0 bg-black-400 transition-[width] duration-300 pointer-events-none"
                    style={{
                        width: overlayWidth,
                        zIndex: 1000,
                    }}
                />
            )}
            {!!blobUrl && !isFetching && !isError && (
                <Image
                    src={blobUrl}
                    alt=""
                    fill
                    className={classNames({
                        "object-cover": display === "cover",
                        "object-contain": display === "contain",
                    })}
                    priority
                    unoptimized
                    draggable={false}
                />
            )}
            {isError && (
                <div className="w-full h-full flex items-center justify-center">
                    <Icons.SmallIcon Icon={Icons.BrokenImage} iconClassName="text-white-300" />
                </div>
            )}
        </div>
    )
}

export function RenderUploadedIconImage({
    entityId,
    imageId,
    allowClick = false,
    allowSelect = false,
    display = "cover",
    isThumbnail = true,
}: {
    entityId: string
    imageId: string
    allowClick?: boolean
    allowSelect?: boolean
    display?: "cover" | "contain"
    isThumbnail?: boolean
}) {
    const dispatch = useAppDispatch()
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const flightId = entityId
    const icon = useAppSelector(selectIconById(entityId))
    const iconImage = useAppSelector(selectIconImageByIdAndIconId(entityId, imageId))
    const isSelected = useAppSelector(selectIsIconImageSelected({ iconId: entityId, imageId }))
    const fileId = useMemo(() => {
        if (!isThumbnail || !iconImage?.hasThumbnail) return imageId
        const dot = imageId.lastIndexOf(".")
        const filenameNoExtension = dot === -1 ? imageId : imageId.slice(0, dot)
        const fileExtension = dot === -1 ? "" : imageId.slice(dot + 1)
        const thumbnailFileName =
            dot === -1 ? imageId : `${filenameNoExtension}.thumbnail.${fileExtension}`
        return thumbnailFileName
    }, [iconImage?.hasThumbnail, imageId, isThumbnail])
    const fetchBlob = useCallback(async () => {
        if (!orgId || !projectId || !flightId) return
        const s3Key = getIconImageS3Key({ orgId, projectId, flightId, fileId })
        const res = await fileManagementApi.fetchFile(s3Key)
        const blob = await res.blob()
        return blob
    }, [orgId, projectId, flightId, fileId])
    const { blobUrl, isFetching, containerRef, isError } = useLazyLoadedImage({
        type: "IconImage",
        imageKey: fileId,
        fetchBlob,
    })
    const imageReady = !!blobUrl && !isFetching && !isError
    return (
        <div
            ref={containerRef}
            className="relative w-full h-full bg-[#1D1D21] group select-none"
            onClick={() => {
                if (!allowClick) return
                dispatch(
                    setPreviewIndex({
                        index: Math.max(
                            0,
                            icon.images.findIndex((image) => image.id === imageId)
                        ),
                    })
                )
                dispatch(
                    setActiveSidePanelModal({
                        id: entityId,
                        layerContentType: "iconimage",
                        type: "layer",
                    })
                )
                dispatch(clearSelection())
            }}
        >
            {allowSelect && (
                <div
                    className={classNames(
                        "absolute top-1 left-1 z-10 group-hover:bg-gray-700 rounded-md",
                        {
                            "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto":
                                !isSelected,
                            "bg-gray-700": isSelected,
                        }
                    )}
                >
                    <TriCheckbox
                        state={isSelected ? "checked" : "unchecked"}
                        onClick={(e) => {
                            e.stopPropagation()
                            dispatch(
                                isSelected
                                    ? deselectIconImage({
                                          iconId: entityId,
                                          imageId,
                                      })
                                    : selectIconImage({
                                          iconId: entityId,
                                          imageId,
                                      })
                            )
                        }}
                    />
                </div>
            )}
            {!imageReady && (
                <div className="absolute inset-0 bg-gray-600 animate-pulse duration-75" />
            )}
            {imageReady && (
                <Image
                    src={blobUrl}
                    alt=""
                    fill
                    className={classNames({
                        "border border-blue-500": isSelected,
                        "object-cover": display === "cover",
                        "object-contain": display === "contain",
                    })}
                    priority
                    unoptimized
                    draggable={false}
                />
            )}
            {isError && (
                <div className="w-full h-full flex justify-center items-center">
                    <Icons.SmallIcon Icon={Icons.BrokenImage} iconClassName="text-white-300" />
                </div>
            )}
        </div>
    )
}
